import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import Playlist from '../../models/playlist/playlist';
import SyncDirection from '../../models/playlist/syncDirection';
import SyncSource from '../../models/playlist/syncSource';
import {
  localIdsForSource,
  replaceSource,
  sourceDirection,
  visibleIds,
} from '../../models/playlist/syncSourceHelpers';
import SyncType from '../../models/playlist/syncType';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import SoundCloudApiTrack from '../../models/soundcloud/soundCloudApiTrack';
import Source from '../../models/source';
import SoundCloudTrack from '../../models/track/soundCloudTrack';
import SyncProvider from '../../models/synchronization/syncProvider';
import { Keys, messages } from '../../settings';
import { SoundCloudApiError } from '../soundCloud/soundCloudApi';
import { ISoundCloudContext } from '../soundCloud/soundCloudService';
import { ILibraryContext } from '../library/libraryService';
import { GlobalState } from '../_globalContext/context';
import { diffSync } from './syncDiff';
import { showLibraryErrors } from '../library/libraryError';

export interface SoundCloudSyncDeps {
  /**
   * Getters rather than values: the provider is queued and may run long after
   * it was created, and it must act on the playlist as it is *now*, not as it
   * was when the sync was requested. See the comment on SyncProvider.
   */
  soundCloud(): ISoundCloudContext;
  lib(): ILibraryContext;
  ctx(): GlobalState;
}

/**
 * Keeps a Fadila playlist and a SoundCloud playlist in step.
 *
 * The comparison is the same three-way set diff YouTube uses (syncDiff.ts).
 * What differs is the writing, and it differs in a way worth knowing about:
 *
 * **SoundCloud has no per-item add or remove.** `PUT /playlists/{urn}` takes
 * the entire track list and deletes anything not in it. That makes a sync one
 * request instead of one per track, with no quota to count and no per-track
 * failure to classify - and it makes a short read catastrophic, because a
 * playlist read halfway and written back is a playlist truncated. So the
 * remote is read to the last page and checked against the playlist's own
 * `track_count` before anything is written; a mismatch abandons the sync with
 * the playlist untouched.
 *
 * Only SoundCloud tracks are looked at: a YouTube video in the same Fadila
 * playlist is invisible here, which is what lets a playlist sync with both
 * services at once. Ordering is not synced - the remote's own order is kept
 * and new tracks are appended.
 */
export default function createSoundCloudSyncProvider(deps: SoundCloudSyncDeps): SyncProvider {
  return async function soundCloudSync(source: SyncSource, playlist: Playlist) {
    const ctx = deps.ctx();
    const scCtx = deps.soundCloud();
    const api = scCtx.soundCloudApi;

    if (source.type !== SyncType.SoundCloudPlaylist) {
      return;
    }

    // Not an error: the user simply has not signed in to SoundCloud in this
    // session. Opening the playlist again after signing in syncs it.
    if (!api) {
      console.debug('SoundCloud sync skipped, not signed in', playlist.name);
      return;
    }

    try {
      // The API wrapper reports its own errors through the snackbar and
      // rethrows, so anything caught below has already been shown.
      const remotePlaylist = await api.getPlaylist(source.sourceId);
      const remoteTracks = await api.getAllPlaylistTracks(source.sourceId);

      // Everything below reads the playlist afresh: the fetches took time and
      // the user may have edited the playlist, or unlinked it, meanwhile.
      const current = deps.lib().playlists.find((pl) => pl.id === playlist.id);
      const currentSource = current?.syncSources.find(
        (x) => x.type === source.type && x.sourceId === source.sourceId,
      );

      if (!current || !currentSource) {
        console.debug('SoundCloud sync abandoned, playlist or source is gone', playlist.name);
        return;
      }

      const direction = sourceDirection(currentSource);

      // What SoundCloud says the playlist holds, against what we managed to
      // read. Equal or better is fine - track_count can lag, and a private or
      // region-blocked track may be counted but not returned. Fewer read than
      // counted is the dangerous direction, and only when we intend to write.
      const remoteComplete = remoteTracks.length >= (remotePlaylist.track_count || 0);

      const diff = diffSync({
        base: currentSource.syncedIds && visibleIds(currentSource, currentSource.syncedIds),
        remote: visibleIds(
          currentSource,
          remoteTracks.map((x) => x.urn),
        ),
        local: visibleIds(currentSource, localIdsForSource(current, currentSource)),
        direction,
        canPush: remoteComplete,
      });

      console.debug('SoundCloud sync', playlist.name, direction, diff);

      // --- Push: Fadila -> SoundCloud ----------------------------------
      //
      // One write, the whole list: the remote's order with the removals taken
      // out and the additions appended. finalRemote is what SoundCloud holds
      // afterwards, and becomes the snapshot - so a write that does not
      // happen leaves its ids as a difference to try again next time.
      let finalRemote = remoteTracks.map((x) => x.urn);
      let wroteRemote = false;

      if (diff.pushAdds.length || diff.pushRemoves.length) {
        const removed = new Set(diff.pushRemoves);
        const next = finalRemote.filter((urn) => !removed.has(urn)).concat(diff.pushAdds);

        try {
          await api.updatePlaylistTracks(source.sourceId, next);
          finalRemote = next;
          wroteRemote = true;
        } catch (err) {
          // Left out of the snapshot deliberately: the next sync sees the
          // same difference and tries again. A permanent failure here is not
          // per-track the way YouTube's is - the whole write either lands or
          // does not - so there is nothing to add to skippedIds.
          console.warn('SoundCloud sync: playlist update failed', err);

          Sentry.withScope((scope) => {
            scope.setExtra('playlist', playlist.name);
            scope.setExtra('soundCloudPlaylistUrn', source.sourceId);
            scope.setExtra('adds', diff.pushAdds.length);
            scope.setExtra('removes', diff.pushRemoves.length);
            scope.setExtra('status', err instanceof SoundCloudApiError ? err.status : undefined);

            Sentry.captureMessage('SoundCloud sync: playlist update failed', Severity.Warning);
          });
        }
      } else if (!remoteComplete && direction !== SyncDirection.FromSource) {
        // Nothing to push this time, but say so once rather than silently
        // never pushing: a playlist we cannot read whole is one we must
        // never write.
        console.warn(
          'SoundCloud sync: read %d of %d tracks, not writing',
          remoteTracks.length,
          remotePlaylist.track_count,
        );
      }

      // --- Pull: SoundCloud -> Fadila ----------------------------------
      //
      // Read the playlist once more; the write above was more network time.
      const latest = deps.lib().playlists.find((pl) => pl.id === playlist.id) || current;
      let tracks = latest.tracks;

      if (diff.pullRemoves.length) {
        const gone = new Set(diff.pullRemoves);

        tracks = tracks.filter((x) => !(x.trackType === Source.SoundCloud && gone.has(x.id)));
      }

      if (diff.pullAdds.length) {
        const wanted = new Set(diff.pullAdds);
        const newTracks: SoundCloudTrack[] = [];

        for (const entry of remoteTracks) {
          if (wanted.has(entry.urn)) {
            // Once per track, however many times the playlist lists it.
            wanted.delete(entry.urn);
            newTracks.push(SoundCloudTrack.from(entry as SoundCloudApiTrack));
          }
        }

        // addToLibrary is a no-op for tracks already in the library, and the
        // playlist gets a reference either way.
        const added = showLibraryErrors(ctx.callSnackbar, () =>
          deps.lib().addToLibrary(...newTracks),
        );

        if (added) {
          tracks = tracks.concat(newTracks.map(PlaylistItem.from));
        }
      }

      const updated = replaceSource(
        { ...latest, tracks },
        {
          ...currentSource,
          lastUpdated: Date.now(),
          syncedIds: Array.from(new Set(finalRemote)),
        },
      );

      // Always saved, even when nothing changed: the snapshot and timestamp did.
      deps.lib().updatePlaylist(updated);

      if (wroteRemote) {
        console.debug('SoundCloud sync wrote playlist', playlist.name, finalRemote.length);
      }
    } catch (err) {
      if (err instanceof SoundCloudApiError) {
        // Already on the snackbar via the api wrapper; a second message would
        // only push the useful one off screen.
        console.warn('SoundCloud sync failed', playlist.name, err);
        return;
      }

      ctx.callSnackbar(messages(Keys.soundCloudSyncFailed));
      console.error(err);
    }
  };
}
